import { Component, computed, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { Automation } from '@vendasta/automata';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { take } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { NodeActionsService } from '../../services/node-actions.service';
import { AutomationViewService } from '../../../automation/automation-view.service';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { EXPANDED_GROUP_END_PREFIX, EXPANDED_GROUP_START_PREFIX } from '../../automation-graph';
import { AutomationGraphUtilitiesService } from '../../automation-graph-utilities.service';
import { MatDialog } from '@angular/material/dialog';
import { SaveSnippetModalComponent } from '../save-snippet-modal/save-snippet-modal.component';
import { GroupModalComponent } from '../group-modal/group-modal.component';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { MatButton } from '@angular/material/button';
import { WorkflowViewerV2Service } from '../../workflow-viewer-v2/workflow-viewer-v2.service';
import { AutomationEditorService } from '../../automation-editor.service';

@Component({
  selector: 'automata-group-node',
  templateUrl: './group-node.component.html',
  styleUrls: ['./group-node.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    MatCard,
    MatCardContent,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    GalaxyPopoverModule,
    MatButton,
  ],
})
export class GroupNodeComponent implements OnInit {
  @Input({ required: true }) automation!: Automation;
  @Input({ required: true }) groupId!: string;
  groupName?: string;
  groupNotes?: string;
  expanded = computed(() => this.automationViewService.expandedGroups().has(this.groupId));
  newlyExpanded = computed(() => this.automationViewService.newlyExpandedGroups().has(this.groupId));
  containerHeight = computed(() => this.getContainerHeight());
  containerWidth = computed(() => this.getContainerWidth());
  containerLeft = computed(() => this.getContainerLeft());
  isOpen = false;

  constructor(
    private nodeActionsService: NodeActionsService,
    private automationViewService: AutomationViewService,
    private automationGraphUtilities: AutomationGraphUtilitiesService,
    private automationEditorService: AutomationEditorService,
    private readonly snackbarService: SnackbarService,
    private readonly workflowViewerV2Service: WorkflowViewerV2Service,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    const group = this.automation?.groups?.find((group) => group.id === this.groupId);
    this.groupName = group?.name;
    this.groupNotes = group?.notes;
  }

  copy() {
    // Get all the steps in this group

    this.nodeActionsService.copyGroup(this.groupId);
  }

  ungroup() {
    this.nodeActionsService.removeGroupFromAutomation(this.automation, this.groupId!).pipe(take(1)).subscribe();
  }

  saveAsActionSet() {
    this.dialog.open(SaveSnippetModalComponent, {
      width: '500px',
      data: { snippetName: this.groupName, groupID: this.groupId },
    });
  }

  deleteGroupClicked(groupId: string): void {
    this.nodeActionsService.deleteGroup(groupId).subscribe({
      next: () => {
        this.snackbarService.openSuccessSnack('AUTOMATIONS.GROUPING.DELETE_GROUP_SUCCESS');
      },
    });
  }

  expand() {
    this.nodeActionsService.expandGroup(this.groupId!);
  }

  collapse(): void {
    this.nodeActionsService.collapseGroup(this.groupId!);
  }

  editDetails() {
    this.isOpen = false; // In case the popover is open
    this.dialog.open(GroupModalComponent, {
      width: '500px',
      data: { groupName: this.groupName, groupId: this.groupId, groupNotes: this.groupNotes },
    });
  }

  private getContainerHeight() {
    if (this.expanded()) {
      const startCoords = document.getElementById(EXPANDED_GROUP_START_PREFIX + this.groupId)?.getBoundingClientRect();
      const endCoords = document.getElementById(EXPANDED_GROUP_END_PREFIX + this.groupId)?.getBoundingClientRect();

      if (startCoords && endCoords) {
        return (endCoords.bottom - startCoords.bottom) / this.getCurrentScale();
      }
      const stepMap = this.automationEditorService.stepMap();

      // Group end step does not exist, calculate the height based on the nodes in the group:
      const nodes: string[] = this.automationGraphUtilities.getAllNodesInGroup(this.groupId);
      const bottomMost =
        nodes.reduce((bottom, node) => {
          const nodeCoords = document.getElementById(node)?.getBoundingClientRect();
          const heightOfNode = (this.workflowViewerV2Service.heightOfNode(node, stepMap) + 20) * this.getCurrentScale();
          const bottomOfNode = (nodeCoords?.bottom || 0) + heightOfNode;
          if (bottomOfNode > bottom) {
            return bottomOfNode;
          }
          return bottom;
        }, 0) +
        20 * this.getCurrentScale(); // Plus padding below

      const difference = bottomMost - (startCoords?.bottom || 0);
      return difference / this.getCurrentScale();
    }
    return 0;
  }

  private getContainerWidth() {
    if (this.expanded()) {
      const nodes: string[] = this.automationGraphUtilities.getAllNodesInGroup(this.groupId);

      // Get the left most element
      const leftMost = nodes.reduce((left, node) => {
        const nodeCoords = document.getElementById(node)?.getBoundingClientRect();
        if (nodeCoords && nodeCoords.left < left) {
          return nodeCoords.left;
        }
        return left;
      }, Infinity);

      // Get the right most element
      const rightMost = nodes.reduce((right, node) => {
        const nodeCoords = document.getElementById(node)?.getBoundingClientRect();
        if (nodeCoords && nodeCoords.right > right) {
          return nodeCoords.right;
        }
        return right;
      }, -Infinity);

      if (leftMost !== Infinity && rightMost !== -Infinity) {
        return (rightMost - leftMost) / this.getCurrentScale() + 24;
      }
    }
    return 380;
  }

  private getContainerLeft() {
    if (this.expanded()) {
      const startCoords = document.getElementById(EXPANDED_GROUP_START_PREFIX + this.groupId)?.getBoundingClientRect();
      const nodes: string[] = this.automationGraphUtilities.getAllNodesInGroup(this.groupId);

      // Get the left most element
      const leftMost = nodes.reduce((left, node) => {
        const nodeCoords = document.getElementById(node)?.getBoundingClientRect();
        if (nodeCoords && nodeCoords.left < left) {
          return nodeCoords.left;
        }
        return left;
      }, Infinity);

      if (startCoords && leftMost !== Infinity) {
        return (leftMost - startCoords.left) / this.getCurrentScale() - 12;
      }
    }
    return 0;
  }

  private zoomWrapper = document.getElementById('zoom-wrapper');

  private getCurrentScale() {
    if (!this.zoomWrapper) {
      return 1;
    }
    const graphStyle = window.getComputedStyle(this.zoomWrapper);
    const graphMatrix = new WebKitCSSMatrix(graphStyle.transform);
    return graphMatrix.m22;
  }

  openPopover() {
    this.isOpen = true;
  }
}
